import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent, ThemeEvents, QuantitySelectorUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Implant Selector Component - Simplified version with server-rendered table
 * Only handles platform switching, quantities, and multi add-to-cart
 */
class ImplantSelectorComponent extends Component {
  loadSettings() {
    const settingsScript = this.querySelector('[data-implant-settings]');
    if (settingsScript) {
      try {
        this.settings = JSON.parse(settingsScript.textContent);
      } catch (error) {
        console.error('❌ Error loading settings:', error);
        this.settings = { texts: {}, metafields: {} };
      }
    } else {
      console.warn('⚠️ Settings script not found');
      this.settings = { texts: {}, metafields: {} };
    }
  }

  loadVariantsData() {
    const variantsScript = this.querySelector('[data-product-variants]');
    if (variantsScript) {
      try {
        this.variantsData = JSON.parse(variantsScript.textContent);
      } catch (error) {
        console.error('❌ Error loading variants data:', error);
        this.variantsData = { variants: [] };
      }
    } else {
      console.warn('⚠️ Variants script not found');
      this.variantsData = { variants: [] };
    }
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Load settings and variants data from JSON
    this.loadSettings();
    this.loadVariantsData();
    
    // Get reference to product info container
    this.productInfoContainer = this.querySelector('[data-product-info]');
    
    // Get references to DOM elements
    this.variantPicker = this.querySelector('variant-picker');
    this.tableBody = this.querySelector('[data-table-body]');
    this.summary = this.querySelector('[data-selection-summary]');
    this.multiAddToCartButton = this.querySelector('[data-multi-add-to-cart]');
    this.addToCartError = this.querySelector('[data-add-to-cart-error]');
    
    // Initialize state
    this.selectedQuantities = new Map();
    this.currentPlatform = null;
    
    // Listen to native variant picker events
    const section = this.closest('.shopify-section') || document;
    section.addEventListener('VariantSelectedEvent', this.handleVariantSelected.bind(this));
    section.addEventListener('VariantUpdateEvent', this.handleVariantUpdate.bind(this));
    
    // Listen to direct changes on the variant picker with capture phase
    if (this.variantPicker) {
      this.boundHandleDirectChange = this.handleDirectChange.bind(this);
      // Use capture phase to intercept before native variant picker
      this.variantPicker.addEventListener('change', this.boundHandleDirectChange, true);
      
      // Also block click events on radio buttons
      this.variantPicker.addEventListener('click', this.handleRadioClick.bind(this), true);
    }
    
    // Add event listener for multi add to cart button
    if (this.multiAddToCartButton) {
      this.multiAddToCartButton.addEventListener('click', this.handleMultiAddToCart.bind(this));
    }
    
    // Initialize - show the correct platform group
    this.initializePlatformDisplay();
    
    // Attach quantity listeners
    this.attachQuantityListeners();
    
    // Attach row click listeners for gallery update
    this.attachRowClickListeners();
    
    // Set up dynamic height for table container
    this.setupDynamicHeight();
    
    // Preload variant gallery images
    this.preloadVariantImages();
  }

  setupDynamicHeight() {
    const calculateOptimalHeight = () => {
      const productMedia = document.querySelector('.product-information__media');
      const variantsTable = this.querySelector('.implant-selector__variants-table');
      
      if (productMedia && variantsTable) {
        const screenWidth = window.innerWidth;
        
        // Only apply dynamic height on desktop (screens > 768px)
        if (screenWidth <= 768) {
          variantsTable.style.maxHeight = '';
          return;
        }
        
        const mediaHeight = productMedia.offsetHeight;
        const productInfo = this.querySelector('.implant-selector__product-info');
        const platformSelector = this.querySelector('.implant-selector__variant-picker');
        const summary = this.querySelector('[data-selection-summary]');
        
        let occupiedHeight = 0;
        if (productInfo) occupiedHeight += productInfo.offsetHeight;
        if (platformSelector) occupiedHeight += platformSelector.offsetHeight;
        if (summary) occupiedHeight += summary.offsetHeight;
        
        let margin;
        if (screenWidth >= 1440) {
          margin = 80;
        } else {
          margin = 140;
        }
        
        const availableHeight = mediaHeight - occupiedHeight - margin;
        const minHeight = 150;
        const maxHeight = screenWidth >= 1440 ? 450 : 350;
        const optimalHeight = Math.max(minHeight, Math.min(maxHeight, availableHeight));
        
        variantsTable.style.maxHeight = `${optimalHeight}px`;
      }
    };
    
    setTimeout(calculateOptimalHeight, 100);
    window.addEventListener('resize', calculateOptimalHeight);
    
    const observer = new ResizeObserver(calculateOptimalHeight);
    const productMedia = document.querySelector('.product-information__media');
    if (productMedia) {
      observer.observe(productMedia);
    }
  }

  preloadVariantImages() {
    if (!this.variantsData?.variants) return;
    
    // Preload first image of each variant with multiple sizes for responsive loading
    // This ensures the main image loads instantly regardless of viewport size
    const preloadedUrls = new Set();
    const widths = [832, 1200, 1600]; // Common viewport sizes
    
    this.variantsData.variants.forEach(variant => {
      if (variant.gallery_images && Array.isArray(variant.gallery_images) && variant.gallery_images.length > 0) {
        const firstImageUrl = variant.gallery_images[0];
        if (!firstImageUrl) return;
        
        // Extract base URL without width parameter
        const baseUrl = firstImageUrl.replace(/&width=\d+/, '').replace(/\?width=\d+/, '');
        
        // Preload multiple sizes of the first image
        widths.forEach(width => {
          const urlWithWidth = baseUrl.includes('?') 
            ? `${baseUrl}&width=${width}` 
            : `${baseUrl}?width=${width}`;
          
          if (!preloadedUrls.has(urlWithWidth)) {
            preloadedUrls.add(urlWithWidth);
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = urlWithWidth;
            document.head.appendChild(link);
          }
        });
      }
    });
  }

  initializePlatformDisplay() {
    if (!this.variantPicker) {
      // For 1-2 options mode, find the group (may or may not have --no-tabs class)
      let singleGroup = this.querySelector('.implant-platform-group--no-tabs');
      if (!singleGroup) {
        // Fallback: find any platform group with data-platform="all"
        singleGroup = this.querySelector('[data-platform="all"]');
      }
      if (!singleGroup) {
        // Last fallback: find any platform group
        singleGroup = this.querySelector('.implant-platform-group');
      }
      
      if (singleGroup) {
        singleGroup.style.display = 'block';
        // Use setTimeout to ensure DOM is fully rendered before selecting first variant
        setTimeout(() => {
          this.autoSelectFirstVariant(singleGroup);
        }, 100);
      }
      this.currentPlatform = 'all';
      return;
    }
    
    const selectedOption = this.variantPicker.querySelector('input[type="radio"]:checked');
    if (selectedOption) {
      if (selectedOption.value !== '3.5') {
        this.showPlatformGroup(selectedOption.value);
      } else {
        this.currentPlatform = '3.5';
      }
    } else {
      const firstOption = this.variantPicker.querySelector('input[type="radio"]');
      if (firstOption) {
        firstOption.checked = true;
        this.currentPlatform = firstOption.value;
        if (firstOption.value !== '3.5') {
          this.showPlatformGroup(firstOption.value);
        }
      }
    }
  }

  handleRadioClick(event) {
    if (event.target.type === 'radio') {
      const selectedValue = event.target.value;
      event.stopPropagation();
      
      setTimeout(() => {
        const isChecked = event.target.checked;
        
        if (!isChecked) {
          const allRadios = this.variantPicker.querySelectorAll('input[type="radio"]');
          allRadios.forEach(radio => radio.checked = false);
          event.target.checked = true;
        }
        
        this.showPlatformGroup(selectedValue);
        this.dispatchVariantSelectedEvent(selectedValue);
      }, 0);
    }
  }

  handleDirectChange(event) {
    if (event.target.type === 'radio') {
      event.stopImmediatePropagation();
      event.preventDefault();
      return false;
    }
  }

  handleVariantSelected(event) {
    const selectedOption = this.variantPicker?.querySelector('input[type="radio"]:checked');
    if (selectedOption) {
      this.showPlatformGroup(selectedOption.value);
    }
  }

  handleVariantUpdate(event) {
    if (event.detail.source === 'implant-selector-component') {
      return;
    }
    
    if (event.detail.html) {
      setTimeout(() => {
        this.restorePlatformState();
      }, 10);
      return;
    }
    
    this.initializePlatformDisplay();
  }

  showPlatformGroup(platformValue) {
    this.currentPlatform = platformValue;
    const allGroups = this.querySelectorAll('.implant-platform-group');
    
    allGroups.forEach((group) => {
      group.style.display = 'none';
      group.style.visibility = 'hidden';
      group.style.opacity = '0';
    });
    
    const targetGroup = this.querySelector(`[data-platform="${platformValue}"]`);
    if (targetGroup) {
      targetGroup.style.display = 'block';
      targetGroup.style.visibility = 'visible';
      targetGroup.style.opacity = '1';
      targetGroup.offsetHeight;
    } else {
      console.warn('⚠️ Platform group not found:', platformValue);
    }
    
    this.updateSummary();
    this.updateProductInfo(platformValue);
    this.autoSelectFirstVariant(targetGroup);
  }

  clearAllQuantities() {
    this.selectedQuantities.clear();
    const quantityInputs = this.querySelectorAll('input[ref="quantityInput"]');
    quantityInputs.forEach(input => {
      input.value = 0;
      input.defaultValue = 0;
    });
    this.updateSummary();
  }

  attachQuantityListeners() {
    this.addEventListener('QuantitySelectorUpdateEvent', (event) => {
      const input = event.target.querySelector('input[ref="quantityInput"]');
      if (!input) return;
      
      const variantId = input.dataset.variantId;
      const newValue = parseInt(input.value) || 0;
      
      this.selectedQuantities.set(variantId, newValue);
      this.updateSummary();
    });

    this.addEventListener('input', (event) => {
      if (event.target.matches('input[ref="quantityInput"]')) {
        const input = event.target;
        const variantId = input.dataset.variantId;
        let newValue = parseInt(input.value) || 0;
        
        // Validate against min/max to prevent incorrect values in summary
        const minVal = parseInt(input.min, 10) || 0;
        const maxVal = input.max ? parseInt(input.max, 10) : Infinity;
        if (newValue < minVal) newValue = minVal;
        if (maxVal !== Infinity && newValue > maxVal) newValue = maxVal;
        
        this.selectedQuantities.set(variantId, newValue);
        this.updateSummary();
      }
    });

    this.addEventListener('click', (event) => {
      if (event.target.matches('button[on\\:click*="Quantity"]')) {
        setTimeout(() => {
          const quantitySelector = event.target.closest('quantity-selector-component');
          const input = quantitySelector?.querySelector('input[ref="quantityInput"]');
          
          if (input) {
            const variantId = input.dataset.variantId;
            const newValue = parseInt(input.value) || 0;
            
            this.selectedQuantities.set(variantId, newValue);
            this.updateSummary();
          }
        }, 50);
      }
    });
  }

  attachRowClickListeners() {
    this.currentSelectedVariantId = null;
    
    this.tableBody?.addEventListener('click', (event) => {
      const row = event.target.closest('.implant-table__row');
      if (!row) return;
      
      const variantId = row.dataset.variantId;
      if (!variantId) return;
      
      if (variantId !== this.currentSelectedVariantId) {
        this.currentSelectedVariantId = variantId;
        this.updateGalleryForVariant(variantId);
        this.highlightSelectedRow(row);
        this.updateProductInfoMetafields(variantId);
        this.dispatchImplantVariantChange(variantId);
      }
    });
  }

  autoSelectFirstVariant(platformGroup) {
    if (!platformGroup) return;
    
    const firstRow = platformGroup.querySelector('.implant-table__row');
    if (!firstRow) return;
    
    const variantId = firstRow.dataset.variantId;
    if (!variantId) return;
    
    this.currentSelectedVariantId = variantId;
    this.highlightSelectedRow(firstRow);
    this.updateGalleryForVariant(variantId);
    this.updateProductInfoMetafields(variantId);
    
    // Dispatch event for sticky bar and other components
    this.dispatchImplantVariantChange(variantId);
  }
  
  dispatchImplantVariantChange(variantId) {
    document.dispatchEvent(new CustomEvent('ImplantVariantChange', {
      detail: { variantId },
      bubbles: true
    }));
  }

  highlightSelectedRow(selectedRow) {
    const allRows = this.querySelectorAll('.implant-table__row');
    allRows.forEach(row => row.classList.remove('implant-table__row--selected'));
    selectedRow.classList.add('implant-table__row--selected');
  }

  updateProductInfoMetafields(variantId) {
    if (!this.productInfoContainer || !this.variantsData?.variants) return;
    
    const variant = this.variantsData.variants.find(v => v.id.toString() === variantId.toString());
    if (!variant || !variant.metafields) return;
    
    const metafieldKeys = ['metafield_1', 'metafield_2', 'metafield_3', 'metafield_4'];
    const metafieldSettings = this.settings?.metafields || {};
    
    metafieldKeys.forEach((key) => {
      const settingKey = metafieldSettings[key];
      if (!settingKey) return;
      
      const metafieldItem = this.productInfoContainer.querySelector(`[data-metafield-key="${settingKey}"]`);
      if (!metafieldItem) return;
      
      const valueSpan = metafieldItem.querySelector('.metafield-value');
      if (!valueSpan) return;
      
      const newValue = variant.metafields[key];
      if (newValue !== null && newValue !== undefined) {
        // Handle boolean values with translations
        let displayValue;
        if (newValue === true || newValue === 'true') {
          displayValue = this.settings?.texts?.booleanYes || 'Yes';
        } else if (newValue === false || newValue === 'false') {
          displayValue = this.settings?.texts?.booleanNo || 'No';
        } else if (typeof newValue === 'string') {
          displayValue = newValue.charAt(0).toUpperCase() + newValue.slice(1);
        } else {
          displayValue = newValue;
        }
        valueSpan.textContent = displayValue;
      } else {
        valueSpan.textContent = '-';
      }
    });
  }

  async updateGalleryForVariant(variantId) {
    const productId = this.dataset.productId;
    if (!productId) return;
    
    const productUrl = window.location.pathname.split('?')[0];
    const requestUrl = `${productUrl}?variant=${variantId}`;
    
    try {
      const response = await fetch(requestUrl);
      if (!response.ok) return;
      
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      
      // Check if there's a valid media-gallery in the response before dispatching event
      const newMediaGallery = html.querySelector('media-gallery');
      if (!newMediaGallery) return;
      
      // Try to get variant data from variant-picker script first
      const variantScript = html.querySelector('variant-picker script[type="application/json"]');
      let variantData = null;
      if (variantScript?.textContent) {
        try {
          variantData = JSON.parse(variantScript.textContent);
        } catch (e) {
          // Could not parse variant data
        }
      }
      
      // If no variant-picker script, create minimal variant data object
      // This is needed for products using implant-selector instead of variant-picker
      if (!variantData || !variantData.id) {
        variantData = { id: parseInt(variantId, 10) };
      }
      
      const section = this.closest('.shopify-section') || document;
      section.dispatchEvent(
        new VariantUpdateEvent(variantData, `variant-${variantId}`, {
          html,
          productId: productId,
          newProduct: undefined,
        })
      );
      
    } catch (error) {
      console.error('Error fetching variant data:', error);
    }
  }

  async handleMultiAddToCart(event) {
    event.preventDefault();
    
    if (this.multiAddToCartButton.disabled) return;    
    
    this.setButtonState('loading');
    this.hideError();    
    
    const itemsToAdd = [];
    
    this.selectedQuantities.forEach((quantity, variantId) => {
      if (quantity > 0) {
        itemsToAdd.push({
          id: variantId,
          quantity: quantity
        });
      }
    });
    
    if (itemsToAdd.length === 0) {
      this.setButtonState('default');
      return;
    }
    
    try {
      const response = await this.addMultipleItemsToCart(itemsToAdd);
      
      if (response.status) {
        this.showError(response.message || this.settings.texts.cartError || 'Error al agregar productos al carrito');
        this.setButtonState('default');
        
        this.dispatchEvent(new CartErrorEvent(
          'implant-selector',
          response.message,
          response.description,
          response.errors
        ));
      } else {
        const totalItems = itemsToAdd.reduce((sum, item) => sum + item.quantity, 0);
        const productId = this.dataset.productId || '';
        
        this.dispatchEvent(new CartAddEvent(
          {},
          itemsToAdd.map(item => item.id).join(','),
          {
            source: 'implant-selector-component',
            itemCount: totalItems,
            productId: productId,
            sections: response.sections
          }
        ));

        this.resetSelections();
        this.setButtonState('success');
        
        setTimeout(() => {
          this.setButtonState('default');
        }, 2000);
      }
    } catch (error) {
      this.showError(this.settings.texts.cartError || 'Error al agregar productos al carrito');
      this.setButtonState('default');
    }
  }

  updateSummary() {
    let totalCount = 0;
    let totalPrice = 0;
    const isPromotionActive = this.promotionToggle ? this.promotionToggle.checked : false;

    this.selectedQuantities.forEach((quantity, variantId) => {
      if (quantity > 0) {
        const input = this.querySelector(`input[data-variant-id="${variantId}"]`);
        if (input) {
          const price = parseFloat(input.dataset.price) || 0;
          totalCount += quantity;
          totalPrice += price * quantity;
        }
      }
    });

    if (isPromotionActive && totalPrice > 0) {
      totalPrice *= 0.9;
    }

    if (this.summary) {
      const countElement = this.summary.querySelector('.summary__count');
      const totalElement = this.summary.querySelector('.summary__total');
      
      if (countElement && totalElement) {
        const plural = totalCount !== 1 ? 's' : '';
        const productsText = this.settings.texts.productsSelected || '{count} producto{plural} seleccionado{plural}';
        const totalText = this.settings.texts.total || 'Total: {price}';
        
        countElement.textContent = productsText
          .replace('{count}', totalCount)
          .replace(/{plural}/g, plural);
        
        const formattedPrice = this.formatMoney(totalPrice);
        totalElement.textContent = totalText.replace('{price}', formattedPrice);
      }
    }

    if (this.multiAddToCartButton) {
      this.multiAddToCartButton.disabled = totalCount === 0;      
      
      const buttonText = this.multiAddToCartButton.querySelector('.button-text');
      if (buttonText) {
        if (totalCount === 0) {
          buttonText.textContent = this.settings.texts.selectProducts || 'Selecciona productos para agregar';
        } else {
          const plural = totalCount !== 1 ? 's' : '';
          const addToCartText = this.settings.texts.addToCart || 'Agregar {count} producto{plural} al carrito';
          buttonText.textContent = addToCartText
            .replace('{count}', totalCount)
            .replace(/{plural}/g, plural);
        }
      }
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }

  async addMultipleItemsToCart(items) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    const formData = new FormData();
    
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity.toString());
    });
    
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });

    return response.json();
  }

  setButtonState(state) {
    if (!this.multiAddToCartButton) return;

    const buttonText = this.multiAddToCartButton.querySelector('.button-text');
    if (!buttonText) return;

    switch (state) {
      case 'loading':
        buttonText.textContent = this.settings.texts.adding || 'Agregando...';
        this.multiAddToCartButton.disabled = true;
        break;
      case 'success':
        buttonText.textContent = this.settings.texts.added || '¡Agregado!';
        this.multiAddToCartButton.disabled = true;
        break;
      case 'default':
      default:
        let hasSelections = false;
        let totalCount = 0;
        this.selectedQuantities.forEach((quantity) => {
          if (quantity > 0) {
            hasSelections = true;
            totalCount += quantity;
          }
        });
        
        if (hasSelections) {
          const plural = totalCount > 1 ? 's' : '';
          const addToCartText = this.settings.texts.addToCart || 'Agregar {count} producto{plural} al carrito';
          buttonText.textContent = addToCartText
            .replace('{count}', totalCount)
            .replace('{plural}', plural);
        } else {
          buttonText.textContent = this.settings.texts.selectProducts || 'Selecciona productos para agregar';
        }
        
        this.multiAddToCartButton.disabled = !hasSelections;
        break;
    }
  }

  showError(message) {
    if (!this.addToCartError) return;

    const errorMessage = this.addToCartError.querySelector('.error-message');
    if (errorMessage) {
      errorMessage.textContent = message;
    }
    
    this.addToCartError.classList.remove('hidden');

    setTimeout(() => {
      this.hideError();
    }, 5000);
  }

  hideError() {
    if (this.addToCartError) {
      this.addToCartError.classList.add('hidden');
    }
  }

  resetSelections() {
    this.selectedQuantities.clear();
    
    const quantityInputs = this.querySelectorAll('input[ref="quantityInput"]');
    quantityInputs.forEach(input => {
      input.value = 0;
      input.defaultValue = 0;
    });
    
    this.updateSummary();
  }

  dispatchVariantSelectedEvent(platformValue) {
    const selectedOption = this.variantPicker?.querySelector(`input[value="${platformValue}"]`);
    if (selectedOption) {
      const variantId = selectedOption.dataset.variantId;
      const optionValueId = selectedOption.dataset.optionValueId;
      
      const target = this.closest('.shopify-section') || document;
      target.dispatchEvent(new VariantSelectedEvent({ 
        id: optionValueId || variantId || '',
        source: 'implant-selector-component'
      }));
    }
  }

  restorePlatformState() {
    if (!this.currentPlatform) {
      this.initializePlatformDisplay();
      return;
    }
    
    const platformOption = this.variantPicker?.querySelector(`input[value="${this.currentPlatform}"]`);
    if (platformOption && !platformOption.checked) {      
      if (this.boundHandleDirectChange) {
        this.variantPicker?.removeEventListener('change', this.boundHandleDirectChange, true);
        this.variantPicker?.removeEventListener('click', this.handleRadioClick.bind(this), true);
      }
      
      platformOption.checked = true;
      this.showPlatformGroup(this.currentPlatform);
      
      setTimeout(() => {
        if (this.boundHandleDirectChange) {
          this.variantPicker?.addEventListener('change', this.boundHandleDirectChange, true);
          this.variantPicker?.addEventListener('click', this.handleRadioClick.bind(this), true);
        }
      }, 50);
    } else {
      this.showPlatformGroup(this.currentPlatform);
    }
  }

  updateProductInfo(platformValue) {
    const platformValueElement = this.querySelector('[data-platform-value]');
    if (platformValueElement) {
      platformValueElement.textContent = `${platformValue} mm`;
    }
    
    const selectedPlatformGroup = this.querySelector(`[data-platform="${platformValue}"]`);
    if (selectedPlatformGroup) {
      const firstVariantRow = selectedPlatformGroup.querySelector('.implant-table__row');
      if (firstVariantRow) {
        const variantId = firstVariantRow.dataset.variantId;
        
        if (this.productData && this.productData.variants) {
          const selectedVariant = this.productData.variants.find(v => v.id.toString() === variantId);
          
          if (selectedVariant) {            
            this.updateMetafieldsDisplay(selectedVariant);
          }
        }
      }
    }
  }

  updateMetafieldsDisplay(variant) {
    const productInfoContainer = this.querySelector('.product-info');
    if (!productInfoContainer) return;
    
    const metafields = [
      { key: 'modeloimplante', label: this.settings?.texts?.modelLabel || 'Modelo' },
      { key: 'conexionimplante', label: this.settings?.texts?.connectionLabel || 'Conexión' },
      { key: 'cuerpoimplante', label: this.settings?.texts?.bodyLabel || 'Cuerpo' }
    ];
    
    metafields.forEach(({ key, label }) => {
      let existingItem = productInfoContainer.querySelector(`[data-metafield="${key}"]`);
      
      if (variant.metafields && variant.metafields.OPX && variant.metafields.OPX[key]) {
        const value = variant.metafields.OPX[key];
        
        if (existingItem) {
          const valueSpan = existingItem.querySelector('.metafield-value');
          if (valueSpan) {
            valueSpan.textContent = value;
          }
        } else {
          const platformItem = productInfoContainer.querySelector('[data-platform-info]');
          const newItem = document.createElement('span');
          newItem.className = 'product-info__item';
          newItem.setAttribute('data-metafield', key);
          newItem.innerHTML = `<strong>${label}:</strong> <span class="metafield-value">${value}</span>`;
          
          if (platformItem) {
            productInfoContainer.insertBefore(newItem, platformItem);
          } else {
            productInfoContainer.appendChild(newItem);
          }
        }        
      } else {
        if (existingItem) {
          existingItem.remove();
        }
      }
    });
  }
}

// Register the custom element
if (!customElements.get('implant-selector-component')) {
  customElements.define('implant-selector-component', ImplantSelectorComponent);
}

export { ImplantSelectorComponent };
